import { useState } from "react";
import { ClipboardList, RotateCcw, Phone } from "lucide-react";
import { JIS_PHONE } from "../../config/site";

const QUESTIONS = [
  "Avez-vous déjà misé plus que prévu ?",
  "Avez-vous déjà voulu vous refaire après une perte ?",
  "Avez-vous caché votre activité de jeu à vos proches ?",
  "Avez-vous déjà emprunté ou vendu quelque chose pour jouer ?",
  "Le jeu vous a-t-il déjà causé du stress ou de l'anxiété ?",
  "Pensez-vous souvent au jeu, même en dehors des moments où vous jouez ?",
  "Avez-vous déjà essayé de réduire ou d'arrêter sans y parvenir ?",
  "Le jeu a-t-il affecté votre travail, vos études ou vos relations ?",
];

export default function ResponsibleQuiz() {
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));

  const answered = answers.filter((a) => a !== null).length;
  const yesCount = answers.filter((a) => a === true).length;
  const done = answered === QUESTIONS.length;

  const result = !done
    ? null
    : yesCount === 0
      ? {
          cls: "bg-[#E8F8F3] text-[#064E3B] border-[#2BB08A]",
          text: "Rien n'indique de difficulté, restez vigilant.",
        }
      : yesCount <= 2
        ? {
            cls: "bg-[#FEF3C7] text-[#78350F] border-[#E8A317]",
            text: "Certains signaux méritent votre attention. Pensez à fixer des limites.",
          }
        : {
            cls: "bg-[#FEF3C7] text-[#78350F] border-[#E8A317]",
            text: `Il serait utile d'en parler. Joueurs Info Service est joignable au ${JIS_PHONE} (appel non surtaxé).`,
          };

  return (
    <div
      data-testid="responsible-quiz"
      className="my-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-semibold text-[#0B1F3A]">
        <ClipboardList className="h-5 w-5 text-[#2BB08A]" aria-hidden="true" />
        Quiz « Où en suis-je avec le jeu ? »
      </h3>
      <p className="mb-5 text-sm text-slate-500">
        8 questions, répondez par oui ou par non, en toute honnêteté. Aucune réponse n'est
        enregistrée.
      </p>

      <ol className="space-y-4">
        {QUESTIONS.map((q, i) => (
          <li key={i} className="rounded-lg bg-[#F3F5F8] p-4">
            <p className="mb-2.5 text-[15px] font-medium text-[#0B1F3A]">
              {i + 1}. {q}
            </p>
            <div className="flex gap-3" role="radiogroup" aria-label={q}>
              {[["Oui", true], ["Non", false]].map(([label, val]) => (
                <button
                  key={label}
                  type="button"
                  role="radio"
                  aria-checked={answers[i] === val}
                  data-testid={`quiz-q${i}-${label.toLowerCase()}`}
                  onClick={() =>
                    setAnswers((a) => a.map((x, idx) => (idx === i ? val : x)))
                  }
                  className={`rounded-full px-5 py-1.5 text-sm font-semibold transition-colors ${
                    answers[i] === val
                      ? "bg-[#0B1F3A] text-white"
                      : "border border-slate-300 text-slate-600 hover:border-slate-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ol>

      <p className="mt-4 text-sm text-slate-500" aria-live="polite">
        {answered} / {QUESTIONS.length} questions répondues
      </p>

      {done && result && (
        <div
          data-testid="quiz-result"
          className={`mt-4 rounded-lg border-l-4 p-4 ${result.cls}`}
          aria-live="polite"
        >
          <p className="font-display text-sm font-semibold uppercase tracking-wider">
            Résultat indicatif — {yesCount} « oui »
          </p>
          <p className="mt-1 text-[15px] font-medium">{result.text}</p>
          <p className="mt-2 text-sm opacity-80">
            Ce quiz est indicatif et ne constitue pas un diagnostic.
          </p>
          <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold">
            <Phone className="h-4 w-4" aria-hidden="true" />
            Joueurs Info Service :{" "}
            <a href="tel:0974751313" className="underline">
              {JIS_PHONE}
            </a>{" "}
            —{" "}
            <a
              href="https://www.joueurs-info-service.fr"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="underline"
            >
              joueurs-info-service.fr
            </a>
          </p>
          <button
            type="button"
            data-testid="quiz-reset"
            onClick={() => setAnswers(Array(QUESTIONS.length).fill(null))}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-current px-4 py-1.5 text-sm font-semibold transition-opacity hover:opacity-80"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Recommencer
          </button>
        </div>
      )}
    </div>
  );
}
